import React, { useEffect } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useGetMyInvitationsQuery } from '../../store/api/localApi';

/**
 * Bridge from the canonical parent form-fill URL
 *   /parent/:studentId/forms/:invitationId/fill
 * to the existing form-fill page
 *   /forms/:formId/fill?invitationId=...
 *
 * The existing FormFill page expects formId in the path and invitationId
 * in the query string. The parent's canonical path has invitationId only;
 * we look up the formId from the user's invitations and redirect.
 *
 * This is a transitional shape. A cleaner end-state would teach FormFill
 * to handle the per-kid path natively, but the redirect keeps the existing
 * surface untouched while making parent links bookmark-friendly.
 */
const ParentFormFillRedirect: React.FC = () => {
  const { studentId, invitationId } = useParams<{
    studentId: string;
    invitationId: string;
  }>();
  const { data, isLoading } = useGetMyInvitationsQuery();
  const navigate = useNavigate();

  // Resolve formId from the parent's invitations once they load.
  // We don't trust client-supplied formIds — the parent wouldn't have a
  // way to construct a wrong one anyway, but routing through the live
  // invitation list is the only place that's authoritative.
  useEffect(() => {
    if (isLoading || !invitationId) return;
    const inv = data?.invitations?.find((i) => i.id === invitationId);
    if (!inv) return;
    navigate(`/forms/${inv.formId}/fill?invitationId=${inv.id}`, { replace: true });
  }, [data, isLoading, invitationId, navigate]);

  if (!invitationId) {
    return <Navigate to={studentId ? `/parent/${studentId}` : '/parent'} replace />;
  }

  // Brief loading state while we look up the formId. The redirect fires
  // as soon as the invitations query resolves; no UI noise to design.
  return (
    <div className="min-h-[40vh] flex items-center justify-center text-sm text-gray-500">
      Opening form…
    </div>
  );
};

export default ParentFormFillRedirect;
